import styled from "styled-components";
import { Link } from "react-router-dom";
import { theme } from "../../styles/theme";

export const HeaderWrapper = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${theme.zIndex.menu};
  backdrop-filter: blur(16px);
  background: rgba(10, 14, 26, 0.85);
  border-bottom: 1px solid ${theme.colors.cardBorder};
  transition: all ${theme.transitions.default};

  &.scrolled {
    padding: ${theme.spacing.md} ${theme.spacing.xl};
    box-shadow: ${theme.shadows.lg};
    border-bottom: 1px solid ${theme.colors.primary}33;
    background: rgba(10, 14, 26, 0.95);
  }

  nav {
    display: flex;
    gap: ${theme.spacing.sm};
    align-items: center;

    @media (max-width: ${theme.breakpoints.md}) {
      display: none;
    }
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

export const Logo = styled(Link)`
  font-size: ${theme.fontSizes["2xl"]};
  font-weight: ${theme.fontWeights.extrabold};
  font-family: ${theme.fonts.heading};
  background: linear-gradient(135deg, #1e90ff 0%, #00d4ff 50%, #f8fafc 100%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  text-decoration: none;
  transition: all ${theme.transitions.default};
  letter-spacing: -0.02em;
  position: relative;
  padding: ${theme.spacing.sm} 0;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 3px;
    background: ${theme.colors.accentGradient};
    border-radius: ${theme.borderRadius.full};
    transform: scaleX(0.2);
    transform-origin: left;
    transition: transform ${theme.transitions.default};
  }

  &:hover {
    filter: brightness(1.2);

    &::after {
      transform: scaleX(1);
    }
  }

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes.xl};
  }
`;

export const NavLink = styled(Link)<{ active?: string }>`
  font-size: ${theme.fontSizes.base};
  font-weight: ${props =>
    props.active ? theme.fontWeights.semibold : theme.fontWeights.normal};
  color: ${props =>
    props.active ? theme.colors.primaryLight : theme.colors.text};
  text-decoration: none;
  position: relative;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.default};
  background: ${props =>
    props.active ? "rgba(30, 144, 255, 0.15)" : "transparent"};

  /* Underline indicator */
  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: ${props => (props.active ? "70%" : "0")};
    height: 2px;
    background: ${theme.colors.accentGradient};
    transform: translateX(-50%);
    transition: width ${theme.transitions.default};
    border-radius: ${theme.borderRadius.full};
  }

  &:hover {
    color: ${theme.colors.primaryLight};
    background: rgba(30, 144, 255, 0.12);

    &::before {
      width: 70%;
    }
  }

  &:active {
    transform: scale(0.96);
  }

  /* Melhor foco para acessibilidade */
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 4px;
  }

  &.active {
    color: ${theme.colors.primaryLight};
    font-weight: ${theme.fontWeights.semibold};
  }
`;

export const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: 2px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  font-size: ${theme.fontSizes.xl};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.default};

  &:hover {
    background: rgba(30, 144, 255, 0.15);
    box-shadow: 0 0 15px rgba(30, 144, 255, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const MobileNav = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(10, 14, 26, 0.98);
  backdrop-filter: blur(20px);
  z-index: ${theme.zIndex.menu - 1};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: ${props => (props.isOpen ? "translateY(0)" : "translateY(-100%)")};
  opacity: ${props => (props.isOpen ? 1 : 0)};
  transition: all ${theme.transitions.slow};
  gap: ${theme.spacing.xl};
`;

export const MobileNavLink = styled(Link)<{ active?: string }>`
  font-size: ${theme.fontSizes["2xl"]};
  font-weight: ${props =>
    props.active ? theme.fontWeights.semibold : theme.fontWeights.normal};
  color: ${props =>
    props.active ? theme.colors.primaryLight : theme.colors.text};
  text-decoration: none;
  transition: all ${theme.transitions.default};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  position: relative;

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: ${props => (props.active ? "80%" : "0")};
    height: 3px;
    background: ${theme.colors.accentGradient};
    transform: translateX(-50%);
    transition: width ${theme.transitions.default};
    border-radius: ${theme.borderRadius.full};
  }

  &:hover {
    color: ${theme.colors.primaryLight};
    background: rgba(30, 144, 255, 0.1);

    &::before {
      width: 80%;
    }
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: 2px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  font-size: ${theme.fontSizes["3xl"]};
  cursor: pointer;
  position: absolute;
  top: ${theme.spacing.xl};
  right: ${theme.spacing.xl};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.default};

  &:hover {
    background: rgba(239, 68, 68, 0.15);
    border-color: #ef4444;
    color: #ef4444;
    box-shadow: 0 0 15px rgba(239, 68, 68, 0.3);
    transform: rotate(90deg);
  }

  &:active {
    transform: rotate(90deg) scale(0.95);
  }
`;
